import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PublicReview } from '@capsico/contracts';
import { useI18n } from '../i18n';
import { hygieneLabel, publicReviewView, summaryCopy } from '../i18n/reviewLabels';
import { REVIEW_SORTS, useLoadMoreSentinel, usePublicReviews } from '../hooks/useReviews';
import { publicReviewHeight } from '../hooks/reviewRules';
import {
  HygieneBadge,
  PhotoViewer,
  PublicReviewCard,
  RatingSummaryCard,
  SortPills,
} from '../components/Reviews';
import { VirtualList } from '../components/VirtualList';
import { Button, EmptyState, ListSkeleton, Notice, PageHeader } from '../components/ui';

/**
 * All reviews of one restaurant (C27): rating summary with the hygiene badge, sort pills
 * (Recent / Highest / Lowest / With photos), then review cards with photos, liked dishes and
 * the restaurant's reply. Pages of 20 load as you scroll; past 100 cards the list is windowed.
 */
export function ReviewsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { outletId = '' } = useParams();
  const reviews = usePublicReviews(outletId);
  const [photo, setPhoto] = useState<string | null>(null);
  const sentinel = useLoadMoreSentinel(reviews.loadMore, reviews.hasMore);

  return (
    <div className="min-h-dvh bg-page pb-10">
      <PageHeader
        title={t('reviews.title')}
        onBack={() => navigate(-1)}
        backLabel={t('common.back')}
      />
      <div className="flex flex-col gap-4 px-4 pt-4">
        {reviews.loading ? (
          <ListSkeleton rows={4} />
        ) : reviews.unavailable ? (
          <EmptyState icon="star" title={t('reviews.empty')}>
            {t('reviews.emptyBody')}
          </EmptyState>
        ) : reviews.failed ? (
          <Notice
            tone="negative"
            supportRef
            title={t('common.wrong')}
            action={
              <Button size="sm" tone="outline" onClick={reviews.retry}>
                {t('common.retry')}
              </Button>
            }
          />
        ) : (
          <>
            {reviews.summary && (
              <RatingSummaryCard
                {...summaryCopy(t, reviews.summary)}
                hygiene={
                  reviews.summary.hygiene ? (
                    <HygieneBadge
                      tone={reviews.summary.hygiene}
                      label={hygieneLabel(t, reviews.summary.hygiene)}
                    />
                  ) : null
                }
              />
            )}
            <SortPills
              label={t('reviews.sort')}
              value={reviews.sort}
              options={REVIEW_SORTS.map((value) => ({
                value,
                label: t(`reviews.sort.${value}`),
              }))}
              onChange={reviews.setSort}
            />
            {reviews.rows.length === 0 ? (
              <EmptyState icon="star" title={t('reviews.empty')}>
                {t('reviews.emptyBody')}
              </EmptyState>
            ) : (
              <VirtualList<PublicReview>
                items={reviews.rows}
                rowHeight={publicReviewHeight}
                getKey={(review) => review.id}
                renderRow={(review) => (
                  <PublicReviewCard
                    review={publicReviewView(t, review)}
                    photoLabel={t('reviews.photo')}
                    onPhoto={setPhoto}
                  />
                )}
              />
            )}
            <div ref={sentinel} aria-hidden="true" />
            {reviews.loadingMore && <ListSkeleton rows={2} />}
            {reviews.hasMore && !reviews.loadingMore && (
              <Button tone="outline" full onClick={reviews.loadMore}>
                {t('reviews.loadMore')}
              </Button>
            )}
            {!reviews.hasMore && reviews.rows.length > 0 && (
              <p className="text-center text-caption text-ink-3">{t('reviews.end')}</p>
            )}
          </>
        )}
      </div>
      {photo && (
        <PhotoViewer url={photo} closeLabel={t('common.back')} onClose={() => setPhoto(null)} />
      )}
    </div>
  );
}
